import { Airport } from "../../../core/flight-static-data";
import { CurrencyValue, JavaCurrencyValue } from "../../../utils";
import { SeatClass } from "../common/FlightSeatClassTray/types";
import { Employee, FlightNonEmployee } from "../common/TravelerPicker/types";

export type FlightSearchSpec = {
  passengers: Employee[];
  nonEmployeeTravelers: FlightNonEmployee;
  airport: {
    origin: string;
    destination: string;
  };
  isRoundTrip: boolean;
  date: {
    depart: Date;
    return: Date;
  };
  seatClass: SeatClass;
  isInternational: boolean;
};

export type FlightSummaryRequest = Pick<
  FlightSearchAPIData,
  "complying" | "journeys"
>;

/**
 * The raw flight data from the API.
 */
export type FlightSearchAPIData = {
  flightId: string;
  complying: Nullable<ComplianceInfo>;
  journeys: Journey[];
  departureAirport: string;
  arrivalAirport: string;
  numOfTransits: string;
  tripDuration: string;
  hasSubclassOption: boolean;
};

export type FlightSmartComboAPIData = {
  departureFlightDetail: SmartComboDepartureResult[];
  returnFlightDetail: SmartComboReturnResult[];
  flightTable: FlightTable[];
};

export type FlightSearchData = NormalFlightData | SmartComboFlightData;

export type NormalFlightData = {
  isSmartCombo: false;
  /**
   * The raw flight data from the API.
   */
  flightSearchAPIData: FlightSearchAPIData;
  /**
   * A summary of flat data from `flightData` for easier and more efficient consumption.
   */
  flightSummary: FlightSummaryData;
};

export type SmartComboFlightData = {
  isSmartCombo: true;
  /**
   * This table index will later be used to map the table data in smart combo
   */
  tableIndex: number;
  smartComboAPIData: SmartComboDepartureResult;
  /**
   * A summary of flat data for easier and more efficient consumption.
   */
  smartComboSummary: FlightSummaryData;
  /**
   * The raw flight data from the API.
   */
  flightSearchAPIData: Nullable<FlightSearchAPIData>;
  /**
   * A summary of flat data from `flightData` for easier and more efficient consumption.
   */
  flightSummary: Nullable<FlightSummaryData>;
};

export type FlightSummaryData = {
  title: string;
  airlines: AirlineSummary[];
  seatClass: string;
  departureDateTime: Date;
  departureDate: string;
  departureTime: string;
  departureAirport: Airport;
  departureLocation: string;
  arrivalDateTime: Date;
  arrivalDate: string;
  arrivalTime: string;
  arrivalAirport: Airport;
  arrivalLocation: string;
  offset: number;
  offsetStr: Nullable<string>;
  duration: number;
  durationStr: string;
  transit: number;
  transitStr: string;
  transitDuration: number;
  transitPoints: string[];
  mainPrice: CurrencyValue;
  mainPriceStr: string;
  originPrice: Nullable<CurrencyValue>;
  originPriceStr: Nullable<string>;
  totalPriceLabel: string;
  totalPrice: CurrencyValue;
  totalPriceStr: string;
  passengerFareSummary: FareSummaryDetail;
  refundable: "REFUNDABLE" | "NON_REFUNDABLE" | "PARTIAL_REFUNDABLE";
  isComplying: boolean;
  /**
   * When there is a transit in the journey, and the seat class for each segments are different.
   */
  isMultiClass: boolean;
  isNonConnectingFlight: boolean;
  isLateNightFlight: boolean;
  isOvernightTransit: boolean;
  hasSubClass: boolean;
};

// This is from Flight Search API
export type ComplianceInfo = {
  errorMessage: string;
  isComplying: boolean;
  reasonNotComplying: "NONE" | "FLIGHT_NOT_COMPLY" | "SEATCLASS_NOT_COMPLY";
};

// Parsed complying data for easier consumption
export type ComplianceSummary = {
  reason: string;
  label: string;
};

export type AirlineSummary = {
  image: {
    src: string;
    /**
     * airline code
     */
    alt: string;
  };
  /**
   * From static data, e.g: "Singapore Airlines" / "Lion Air".
   *
   * There will be a possibility that this value is undefined, because the static data is not reliable.
   */
  name: string | undefined;
  /**
   * The airline code from the backend. e.g: "SQ" / "JT"
   */
  code: string;
  flightCode: string;
  facilities: Facility[];
  hasBaggage: boolean;
  hasInFlightMeal: boolean;
};

export type Journey = {
  segments: Segment[];
  arrivalDetail: Omit<ArrivalDetail, "airport">;
  departureDetail: Omit<DepartureDetail, "airport">;
  numOfTransits: string;
  journeyDuration: string;
  // API contract stated this is nullable, but it makes no sense
  fareInfo: FareInfo;
  daysOffset: string;
  refundableStatus: RefundableStatus;
};

export type Segment = {
  flightCode: string;
  marketingAirline: string;
  brandAirline: string;
  operatingAirline: string;
  subClass: Nullable<string>;
  seatClass: SeatClass;
  flightDurationInMinutes: string;
  transitDurationInMinutes: Nullable<string>;
  departureDetail: Omit<DepartureDetail, "airport">;
  arrivalDetail: Omit<ArrivalDetail, "airport">;
  stopInfo: Nullable<StopInfo>;
  addOns: AddOnsOptions;
  mayNeedReCheckIn: Nullable<boolean>;
  visaRequired: Nullable<boolean>;
};

export type ArrivalDetail = {
  airport: Airport;
  airportCode: string;
  arrivalDate: string;
  arrivalTime: string;
  arrivalTerminal: Nullable<string>;
};

export type DepartureDetail = {
  airport: Airport;
  airportCode: string;
  departureDate: string;
  departureTime: string;
  departureTerminal: Nullable<string>;
};

type StopInfo = {
  airportCode: string;
  durationMinutes: string;
};

export type FareSummaryDetail = {
  adults: {
    mainPrice: CurrencyValue;
    originPrice: Nullable<CurrencyValue>;
  };
  children: {
    mainPrice: CurrencyValue;
    originPrice: Nullable<CurrencyValue>;
  };
  infants: {
    mainPrice: CurrencyValue;
    originPrice: Nullable<CurrencyValue>;
  };
};

export type FareInfo = {
  partnerFare: FareDetail;
  airlineFare: FareDetail;
};

export type FareDetail = {
  adultFare: PaxFareDetail;
  childFare: Nullable<PaxFareDetail>;
  infantFare: Nullable<PaxFareDetail>;
};

export type PaxFareDetail = {
  baseFareWithCurrency: JavaCurrencyValue;
  vatWithCurrency: Nullable<JavaCurrencyValue>;
  pscWithCurrency: Nullable<JavaCurrencyValue>;
  fuelSurchargeWithCurrency: Nullable<JavaCurrencyValue>;
  adminFeeWithCurrency: Nullable<JavaCurrencyValue>;
  additionalFeeWithCurrency: Nullable<JavaCurrencyValue>;
  totalFareWithCurrency: JavaCurrencyValue;
};

export type AddOnsOptions = {
  baggageOptions: BaggageAddOns[];
  mealOptions: MealAddOns[];
};

export type BaggageAddOns = {
  id: string;
  baggageType: "PIECE" | "KG";
  baggageWeight: string;
  baggageQuantity: string;
  priceWithCurrency: JavaCurrencyValue;
  departureAirport?: string;
  arrivalAirport?: string;
};

export type MealAddOns = {
  id: string;
  quantity: string;
  displayName: string;
  priceWithCurrency: JavaCurrencyValue;
};

export enum RefundableStatus {
  REFUNDABLE = "REFUNDABLE",
  NON_REFUNDABLE = "NON_REFUNDABLE",
  UNKNOWN = "UNKNOWN",
}

type GeneralFacility = {
  type: "baggage_paid" | "meal";
  description: string;
};

type BaggageFacility = {
  type: "baggage";
  description: string;
  weight: number;
  quantity: number;
};

export type Facility = GeneralFacility | BaggageFacility;

// Flight Smart Combo
export type SmartComboDepartureResult = {
  complying: ComplianceInfo;
  journeys: Journey[];
};

export type SmartComboReturnResult = {
  complying: ComplianceInfo;
  journeys: Array<Omit<Journey, "fareInfo"> & { fareInfo: null }>;
};

export type FlightTable = Array<{
  indexReturn: string;
  flightId: string;
  returnFlightFareInfo: FareInfo;
}>;
